import React, { useMemo } from 'react';
import './FlatRateRowsEditor.css';
import ActionButton from '../input/ActionButton';
import CustomDropdown from '../input/CustomDropdown';
import Textfield from '../input/Textfield';
import useRoleSettings from '../../hooks/useRoleSettings';
import { ROLE_ORDER, normalizeRole, roleLabel } from '../../util/participantGroups';
import { parsePriceInput, samePrice, toPriceInput } from '../../util/eventPrices';
import {
  DELETED_PERSON_LABEL, FLAT_RATE_AMOUNT_MAX, FLAT_RATE_KIND, dueLabel, flatRateAmountAt, flatRateKindIcon,
  flatRateLocationAmounts, formatFlatRateAmount, isDeduction, normalizeFlatRateKind, parseRecipientValue,
  recipientLabel, recipientValue,
} from '../../util/flatRates';

// ── Row model ──────────────────────────────────────────────────────────────────────────────────
// One assignment row of a template ("Standard-Pauschalen/-Abzüge") or a process:
// { key, id, kind, flatRateId, role, playerId, amount, flatRateName, recipientName, catalogAmount }
// - key: client key (React key, stable while editing) — the server id is kept separately (null = new).
// - kind: FLAT_RATE_KIND of the catalog entry — a deduction ("Abzug") has no recipient (role and playerId
//   always null).
// - role / playerId: the recipient of a flat rate, exactly one set (both null = stored person row whose
//   player was deleted, process rows only).
// - amount: input string of process rows (withAmount); template rows have none (catalog amount).
// - flatRateName / recipientName / catalogAmount: what the server sent with the row — labels while the
//   catalog and the candidates load, or when the referenced entry is not in them. catalogAmount is the
//   catalog standard (null = "entfällt").
let keySeq = 0;
export const nextFlatRateRowKey = () => `flatRate${++keySeq}`;

export const newFlatRateRow = (kind = FLAT_RATE_KIND.flatRate) => ({
  key: nextFlatRateRowKey(),
  id: null,
  kind: normalizeFlatRateKind(kind),
  flatRateId: null,
  role: null,
  playerId: null,
  amount: '',
  flatRateName: '',
  recipientName: '',
  catalogAmount: null,
});

// Stored person row whose player was hard-deleted: kept as it is ("Gelöschte Person"). Deductions have no
// recipient at all.
export const isDeletedPersonRow = (row) => !isDeduction(row) && row?.id != null && recipientValue(row) === '';

// Problems per row (Map key → texts), in the wording of the server validator. Duplicates are flagged on
// the later row (flat rate: same entry for the same recipient; deduction: same entry twice); deleted-person
// rows are never duplicates (their recipients are unknown). Rows of both kinds may be mixed.
export function flatRateRowErrors(rows, { withAmount = false } = {}) {
  const errors = new Map();
  const seen = new Set();
  (rows ?? []).forEach(row => {
    const list = [];
    const deduction = isDeduction(row);
    const recipient = deduction ? '' : recipientValue(row);
    if (!deduction && !recipient && !isDeletedPersonRow(row)) list.push('Bitte einen Empfänger wählen.');
    if (row.flatRateId == null) list.push(deduction ? 'Bitte einen Abzug wählen.' : 'Bitte eine Pauschale wählen.');
    if (withAmount) {
      const amount = parsePriceInput(row.amount);
      if (amount == null) list.push('Bitte einen Betrag angeben.');
      else if (Number.isNaN(amount) || amount > FLAT_RATE_AMOUNT_MAX) list.push('Der Betrag muss zwischen 0 und 10.000 € liegen.');
    }
    if (row.flatRateId != null && (deduction || recipient)) {
      const pair = deduction ? `deduction|${row.flatRateId}` : `${row.flatRateId}|${recipient}`;
      if (seen.has(pair)) {
        list.push(deduction ? 'Dieser Abzug ist schon eingetragen.' : 'Diese Pauschale ist für denselben Empfänger schon eingetragen.');
      }
      seen.add(pair);
    }
    if (list.length > 0) errors.set(row.key, list);
  });
  return errors;
}

// One message for the whole list ('' = fine) — the rows show the details.
export const flatRateRowsIssue = (rows, options) => (flatRateRowErrors(rows, options).size > 0
  ? 'Pauschalen & Abzüge: Bitte die markierten Einträge prüfen.'
  : '');

const recipientIcon = (row) => {
  if (normalizeRole(row.role)) return 'shield_person';
  return isDeletedPersonRow(row) ? 'person_off' : 'person';
};

// Catalog standard of a row as a number (null = "entfällt"): the loaded catalog entry, else what the
// server sent with the row.
function standardAmount(catalogEntry, row) {
  if (catalogEntry) return flatRateAmountAt(catalogEntry, null).amount;
  const raw = row?.catalogAmount;
  return raw == null || raw === '' ? null : Number(raw);
}

// "Halle Pforzheim 500,00 €, Eigenes Gelände entfällt" — '' without deviations.
const deviationsText = (catalogEntry) => flatRateLocationAmounts(catalogEntry)
  .map(d => `${d.locationName || `Location #${d.locationId}`} ${formatFlatRateAmount(d.amount)}`)
  .join(', ');

/**
 * Row editor for the assignments of ONE kind of catalog entry — flat rates ("Pauschalen", kind FlatRate:
 * recipient + entry) or deductions ("Abzüge", kind Deduction: entry only, paid externally). Shared by the
 * template editor (Standard-Pauschalen/-Abzüge, catalog amount applies) and the process page (withAmount:
 * own amount per row, prefilled with the catalog amount at the process location `locationId`). One row =
 * [recipient] + catalog entry (+ amount) + remove; "Pauschale/Abzug hinzufügen" appends.
 * Controlled: `rows` (only rows of this kind) in, `onChange(nextRows)` out. Errors per row only after a
 * save attempt (showErrors). flatRates: catalog [{ id, name, kind, amount, locationAmounts }]
 * (hooks/useFlatRates, both kinds — filtered here); candidates / candidatesById: current role holders
 * (hooks/useFlatRateRecipients, unused for deductions) — both may still be loading.
 * title: optional small heading of the area ("Pauschalen" / "Abzüge").
 */
function FlatRateRowsEditor({
  rows, onChange, flatRates, flatRatesLoaded, flatRatesFailed = false, candidates, candidatesById,
  candidatesLoaded = true, candidatesFailed = false, withAmount = false, showErrors = false, emptyText,
  disabled = false, kind = FLAT_RATE_KIND.flatRate, locationId = null, locationName = '', title,
}) {
  useRoleSettings(); // re-render once the configured role names arrive
  const editorKind = normalizeFlatRateKind(kind);
  const deductionMode = editorKind === FLAT_RATE_KIND.deduction;
  const kindLabel = deductionMode ? 'Abzug' : 'Pauschale';
  const list = rows ?? [];
  // Only entries of this kind are offered (the catalog holds both).
  const catalog = useMemo(
    () => (flatRates ?? []).filter(f => normalizeFlatRateKind(f.kind) === editorKind),
    [flatRates, editorKind],
  );
  const persons = candidates ?? [];
  const catalogById = useMemo(() => new Map(catalog.map(f => [Number(f.id), f])), [catalog]);
  const errors = showErrors ? flatRateRowErrors(list, { withAmount }) : null;
  // Catalog placeholder while not loaded: a failed load must not look like it is still running.
  const loadingText = flatRatesFailed ? 'Konnte nicht geladen werden' : 'Lädt …';
  const atLocation = locationName ? `an ${locationName}` : 'an dieser Location';
  const forLocation = locationName ? `für ${locationName}` : 'für diese Location';

  const patchRow = (key, changes) => onChange(list.map(r => (r.key === key ? { ...r, ...changes } : r)));
  const removeRow = (key) => onChange(list.filter(r => r.key !== key));
  const addRow = () => onChange([...list, newFlatRateRow(editorKind)]);

  const changeRecipient = (row, value) => {
    const { role, playerId } = parseRecipientValue(value);
    if (!role && playerId == null) return;
    patchRow(row.key, { role, playerId, recipientName: playerId != null ? (candidatesById?.get(playerId)?.name ?? '') : '' });
  };

  // Picking an entry prefills the amount (catalog amount at the process location) while it is empty or
  // still the amount of the previously picked entry; "entfällt" there leaves the field empty.
  const changeFlatRate = (row, value) => {
    const flatRateId = value ? Number(value) : null;
    const next = flatRateId != null ? catalogById.get(flatRateId) : null;
    const changes = { flatRateId, flatRateName: next?.name ?? '', catalogAmount: next?.amount ?? null };
    if (withAmount && next) {
      const previous = row.flatRateId != null ? catalogById.get(Number(row.flatRateId)) : null;
      const previousAmount = previous ? flatRateAmountAt(previous, locationId).amount : null;
      const nextAmount = flatRateAmountAt(next, locationId).amount;
      const current = parsePriceInput(row.amount);
      const untouched = current == null
        || (previousAmount != null && !Number.isNaN(current) && samePrice(current, previousAmount));
      if (untouched) changes.amount = nextAmount != null ? toPriceInput(nextAmount) : '';
    }
    patchRow(row.key, changes);
  };

  // Template rows: catalog standard + deviations ("300,00 € je Event · Standard aus dem Katalog · abweichend: …").
  const templateDue = (row, catalogEntry) => {
    const standard = standardAmount(catalogEntry, row);
    const deviations = deviationsText(catalogEntry);
    return (
      <>
        {standard != null
          ? <><strong>{formatFlatRateAmount(standard)}</strong> {dueLabel(row)} · Standard aus dem Katalog</>
          : <><strong>entfällt</strong> im Standard</>}
        {deviations && <> · abweichend: {deviations}</>}
      </>
    );
  };

  // Process rows: due rule + catalog amount at the process location when the row amount differs from it.
  const processDue = (row, catalogEntry) => {
    let hint = null;
    if (catalogEntry) {
      const { amount: catalogAmount, deviation } = flatRateAmountAt(catalogEntry, locationId);
      const amount = parsePriceInput(row.amount);
      if (catalogAmount == null) {
        hint = <> · laut Katalog entfällt {atLocation}</>;
      } else if (amount != null && !Number.isNaN(amount) && !samePrice(amount, catalogAmount)) {
        hint = deviation
          ? <> · im Katalog {forLocation}: {formatFlatRateAmount(catalogAmount)}</>
          : <> · im Katalog: {formatFlatRateAmount(catalogAmount)}</>;
      }
    }
    return <>Fällig {dueLabel(row)}{hint}</>;
  };

  const renderRow = (row) => {
    const value = deductionMode ? '' : recipientValue(row);
    const deleted = !deductionMode && isDeletedPersonRow(row);
    const isRole = !deductionMode && !!normalizeRole(row.role);
    const playerId = !deductionMode && row.playerId != null ? Number(row.playerId) : null;
    // Person of a stored row that holds no role anymore (or candidates still loading): keep it selectable.
    const personMissing = !isRole && playerId != null && !candidatesById?.has(playerId);
    const name = deductionMode ? '' : recipientLabel(row, candidatesById);
    const flatRateId = row.flatRateId != null ? Number(row.flatRateId) : null;
    const catalogEntry = flatRateId != null ? catalogById.get(flatRateId) : null;
    const entryName = catalogEntry?.name || row.flatRateName;
    const rowName = deductionMode
      ? (entryName || 'Neuer Abzug')
      : `${value || deleted ? name : 'Neue Pauschale'}${entryName ? ` – ${entryName}` : ''}`;
    const rowErrors = errors?.get(row.key) ?? [];
    const amountPlaceholder = withAmount && catalogEntry ? flatRateAmountAt(catalogEntry, locationId).amount : null;

    // Amount + due rule of the row ("20,00 € je Event"): a flat rate once its recipient is chosen (the rule
    // depends on it), a deduction once its entry is chosen.
    let due = null;
    if (deductionMode ? flatRateId != null : (value || deleted)) {
      if (withAmount) due = processDue(row, catalogEntry);
      else due = flatRateId != null ? templateDue(row, catalogEntry) : <>Fällig {dueLabel(row)}</>;
    }

    return (
      <li
        key={row.key}
        className={`flatRateRow${deductionMode ? ' isDeduction' : ''}${withAmount ? ' withAmount' : ''}${rowErrors.length > 0 ? ' hasErrors' : ''}`}
      >
        <div className="flatRateRowFields">
          {!deductionMode && (
            <div className="textFieldWrapper flatRateRowSelect flatRateRowRecipient">
              <label className="textFieldLabel">Empfänger</label>
              <CustomDropdown
                icon={recipientIcon(row)}
                value={value}
                ariaLabel={`Empfänger für ${rowName}`}
                onChange={e => changeRecipient(row, e.target.value)}
              >
                <option value="" disabled>{deleted ? DELETED_PERSON_LABEL : 'Empfänger wählen …'}</option>
                <optgroup label="Rollen">
                  {ROLE_ORDER.map(role => (
                    <option key={role} value={`role:${role}`}>{roleLabel(role)}</option>
                  ))}
                </optgroup>
                <optgroup label="Personen">
                  {personMissing && (
                    <option value={value}>{candidatesLoaded ? `${name} (keine Rolle mehr)` : name}</option>
                  )}
                  {persons.map(c => (
                    <option key={c.playerId} value={`player:${c.playerId}`}>{c.name}</option>
                  ))}
                  {!candidatesLoaded && persons.length === 0 && (
                    <option value="__loading" disabled>{candidatesFailed ? 'Konnte nicht geladen werden' : 'Lädt …'}</option>
                  )}
                  {candidatesLoaded && persons.length === 0 && !personMissing && (
                    <option value="__none" disabled>Niemand hat aktuell eine Rolle</option>
                  )}
                </optgroup>
              </CustomDropdown>
            </div>
          )}

          <div className="textFieldWrapper flatRateRowSelect flatRateRowRate">
            <label className="textFieldLabel">{kindLabel}</label>
            <CustomDropdown
              icon={flatRateKindIcon(editorKind)}
              value={flatRateId != null ? String(flatRateId) : ''}
              ariaLabel={deductionMode ? `Abzug (${rowName})` : `Pauschale für ${rowName}`}
              onChange={e => changeFlatRate(row, e.target.value)}
            >
              <option value="" disabled>{flatRatesLoaded ? `${kindLabel} wählen …` : loadingText}</option>
              {flatRateId != null && !catalogEntry && (
                <option value={String(flatRateId)}>
                  {row.flatRateName
                    ? (withAmount ? row.flatRateName : `${row.flatRateName} · ${formatFlatRateAmount(standardAmount(null, row))}`)
                    : (flatRatesLoaded ? `${kindLabel} #${flatRateId}` : loadingText)}
                </option>
              )}
              {/* With an own amount field the catalog amount is prefilled there (and hinted when it differs). */}
              {catalog.map(f => (
                <option key={f.id} value={String(f.id)}>
                  {withAmount ? f.name : `${f.name} · ${formatFlatRateAmount(standardAmount(f))}`}
                </option>
              ))}
            </CustomDropdown>
          </div>

          {withAmount && (
            <div className="flatRateRowAmount">
              <Textfield
                label="Betrag (€)"
                icon="euro"
                type="number"
                min={0}
                max={FLAT_RATE_AMOUNT_MAX}
                step={0.01}
                inputMode="decimal"
                value={row.amount ?? ''}
                placeholder={amountPlaceholder != null ? toPriceInput(amountPlaceholder) : '0.00'}
                aria-label={`Betrag für ${rowName}`}
                onChange={e => patchRow(row.key, { amount: e.target.value })}
              />
            </div>
          )}

          <button
            type="button"
            className="flatRateRowBtn isDanger"
            onClick={() => removeRow(row.key)}
            disabled={disabled}
            aria-label={`${rowName} entfernen`}
            title={`${kindLabel} entfernen`}
          >
            <span className="material-symbols-rounded" aria-hidden="true">delete</span>
          </button>
        </div>

        {due && (
          <p className="flatRateRowDue">
            <span className="material-symbols-rounded" aria-hidden="true">{isRole ? 'groups' : 'event_repeat'}</span>
            <span>{due}</span>
          </p>
        )}

        {rowErrors.length > 0 && (
          <ul className="flatRateRowErrors">
            {rowErrors.map((text, i) => (
              <li key={i}>
                <span className="material-symbols-rounded" aria-hidden="true">error</span>
                {text}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className="flatRateRows">
      {title && <h4 className="flatRateRowsTitle">{title}</h4>}

      {list.length === 0
        ? <p className="flatRateRowsEmpty">{emptyText ?? (deductionMode ? 'Keine Abzüge.' : 'Keine Pauschalen.')}</p>
        : <ul className="flatRateRowList">{list.map(renderRow)}</ul>}

      {flatRatesLoaded && catalog.length === 0 && (
        <p className="flatRateRowsHint">
          <span className="material-symbols-rounded" aria-hidden="true">info</span>
          {deductionMode
            ? 'Es gibt noch keine Abzüge – lege Abzüge zuerst auf der Seite „Pauschalen & Abzüge“ an (Kachel auf der Startseite).'
            : 'Es gibt noch keine Pauschalen – lege sie zuerst auf der Seite „Pauschalen & Abzüge“ an (Kachel auf der Startseite).'}
        </p>
      )}

      <div className="flatRateRowsAdd">
        <ActionButton icon="add" label={`${kindLabel} hinzufügen`} type={3} onClick={addRow} disabled={disabled} />
      </div>
    </div>
  );
}

export default FlatRateRowsEditor;
